import { Plus } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { Chip } from '~/components/ui/chip';

interface TagsEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  label: string;
  /** Extra explanation, shown as the label's tooltip. */
  tooltip?: string;
  /** What one value is, in the controls' names (`Add customer ID`); defaults to `tag`. */
  noun?: string;
  /** Chips only: no `×` and no add button. */
  readOnly?: boolean;
  testId: string;
}

/** Chips with a remove `×`, plus an `Add tag` button that opens an inline input. */
export function TagsEditor({
  tags,
  onChange,
  label,
  tooltip,
  noun = 'tag',
  readOnly = false,
  testId,
}: TagsEditorProps) {
  const labelId = useId();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const cancelled = useRef(false);

  function finish() {
    if (cancelled.current) {
      cancelled.current = false;
    } else {
      const tag = draft.trim();
      if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    }
    setDraft('');
    setAdding(false);
  }

  return (
    <div data-testid={testId}>
      <span
        id={labelId}
        title={tooltip}
        className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {label}
      </span>
      <div role="group" aria-labelledby={labelId} className="flex flex-wrap items-center gap-2">
        {tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            onRemove={readOnly ? undefined : () => onChange(tags.filter((t) => t !== tag))}
            removeLabel={`Remove ${noun} ${tag}`}
            testId={`${testId}-tag-${tag}`}
          />
        ))}
        {readOnly ? null : adding ? (
          <input
            autoFocus
            type="text"
            value={draft}
            aria-label={`New ${noun}`}
            data-testid={`${testId}-input`}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={finish}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                finish();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                cancelled.current = true;
                setDraft('');
                setAdding(false);
              }
            }}
            className="h-7 w-32 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          />
        ) : (
          <button
            type="button"
            aria-label={`Add ${noun}`}
            title={`Add ${noun}`}
            data-testid={`${testId}-add`}
            onClick={() => {
              // Some browsers fire no blur when Escape unmounts the input, so clear its cancel here.
              cancelled.current = false;
              setAdding(true);
            }}
            className="focus-ring inline-flex size-7 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5"
          >
            <Plus className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Table cell for tags: the first two chips and `+n` for the rest; `—` when there are none. */
export function TagsCell({ tags }: { tags: string[] }) {
  if (tags.length === 0) return <span className="text-gray-500 dark:text-gray-400">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {tags.slice(0, 2).map((tag) => (
        <Chip key={tag} label={tag} />
      ))}
      {tags.length > 2 ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">+{tags.length - 2}</span>
      ) : null}
    </span>
  );
}
